import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  DEFAULT_PROTOTYPER_GENERATION_MODEL,
  type PrototyperGenerationModel,
} from '../../types/prototyper.types';

// ============================================================================
// STATE TYPE
// ============================================================================

export interface PrototyperState {
  // Current prototyper data
  currentPrototyper: {
    demo_url: string | null;
    prototyper_demo_link_id: number | null;
    slug: string | null;
    header: string | null;
    isNewlyGenerated?: boolean;
  };

  // Loading and error states
  isCreatingPrototype: boolean;
  isFetchingPrototype: boolean;
  prototypeError: string | null;

  // Prototype URL (for newly created prototypes)
  prototypeUrl: string | null;

  /** LLM for POST /prototype only (not conversation llm_family) */
  prototypeModelProvider: string;
  prototypeModelChoice: string;
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: PrototyperState = {
  currentPrototyper: {
    demo_url: null,
    prototyper_demo_link_id: null,
    slug: null,
    header: null,
  },
  isCreatingPrototype: false,
  isFetchingPrototype: false,
  prototypeError: null,
  prototypeUrl: null,
  prototypeModelProvider: DEFAULT_PROTOTYPER_GENERATION_MODEL.modelProvider,
  prototypeModelChoice: DEFAULT_PROTOTYPER_GENERATION_MODEL.modelChoice,
};

// ============================================================================
// SLICE
// ============================================================================

const prototyperSlice = createSlice({
  name: 'prototyper',
  initialState,
  reducers: {
    // Set creation loading state
    setIsCreatingPrototype: (state, action: PayloadAction<boolean>) => {
      state.isCreatingPrototype = action.payload;
      if (action.payload) {
        state.prototypeError = null;
      }
    },

    // Set fetching loading state
    setIsFetchingPrototype: (state, action: PayloadAction<boolean>) => {
      state.isFetchingPrototype = action.payload;
      if (action.payload) {
        state.prototypeError = null;
      }
    },

    // Set prototype error
    setPrototypeError: (state, action: PayloadAction<string | null>) => {
      state.prototypeError = action.payload;
      state.isCreatingPrototype = false;
      state.isFetchingPrototype = false;
    },

    // Set prototype URL (for newly created)
    setPrototypeUrl: (state, action: PayloadAction<string | null>) => {
      state.prototypeUrl = action.payload;
    },

    // Set current prototyper data
    setCurrentPrototyper: (
      state,
      action: PayloadAction<{
        demo_url: string | null;
        prototyper_demo_link_id: number | null;
        slug: string | null;
        header: string | null;
        isNewlyGenerated?: boolean;
      }>
    ) => {
      state.currentPrototyper = action.payload;
      if (action.payload.demo_url) {
        state.prototypeUrl = action.payload.demo_url;
      }
    },

    // Update prototyper slug only
    updatePrototyperSlug: (state, action: PayloadAction<string | null>) => {
      state.currentPrototyper.slug = action.payload;
    },

    // Update prototyper header only
    updatePrototyperHeader: (state, action: PayloadAction<string | null>) => {
      state.currentPrototyper.header = action.payload;
    },

    // Clear prototyper state
    clearPrototyper: (state) => {
      state.currentPrototyper = {
        demo_url: null,
        prototyper_demo_link_id: null,
        slug: null,
        header: null,
      };
      state.prototypeUrl = null;
      state.prototypeError = null;
      state.isCreatingPrototype = false;
      state.isFetchingPrototype = false;
    },

    // Clear only error
    clearPrototypeError: (state) => {
      state.prototypeError = null;
    },

    setPrototypeGenerationModel: (
      state,
      action: PayloadAction<{ modelProvider: string; modelChoice: string }>
    ) => {
      state.prototypeModelProvider = action.payload.modelProvider;
      state.prototypeModelChoice = action.payload.modelChoice;
    },
  },
});

// ============================================================================
// EXPORTS
// ============================================================================

export const {
  setIsCreatingPrototype,
  setIsFetchingPrototype,
  setPrototypeError,
  setPrototypeUrl,
  setCurrentPrototyper,
  updatePrototyperSlug,
  updatePrototyperHeader,
  clearPrototyper,
  clearPrototypeError,
  setPrototypeGenerationModel,
} = prototyperSlice.actions;

// Selectors (with defensive checks)
export const selectPrototyperState = (state: { prototyper: PrototyperState }) =>
  state.prototyper ?? initialState;
export const selectCurrentPrototyper = (state: { prototyper: PrototyperState }) =>
  state.prototyper?.currentPrototyper ?? initialState.currentPrototyper;
export const selectIsCreatingPrototype = (state: { prototyper: PrototyperState }) =>
  state.prototyper?.isCreatingPrototype ?? false;
export const selectIsFetchingPrototype = (state: { prototyper: PrototyperState }) =>
  state.prototyper?.isFetchingPrototype ?? false;
export const selectPrototypeError = (state: { prototyper: PrototyperState }) =>
  state.prototyper?.prototypeError ?? null;
export const selectPrototypeUrl = (state: { prototyper: PrototyperState }) =>
  state.prototyper?.prototypeUrl ?? null;

export const selectPrototypeGenerationModel = (state: {
  prototyper: PrototyperState;
}): Pick<PrototyperGenerationModel, 'modelProvider' | 'modelChoice'> => {
  const s = state.prototyper ?? initialState;
  return {
    modelProvider: s.prototypeModelProvider ?? initialState.prototypeModelProvider,
    modelChoice: s.prototypeModelChoice ?? initialState.prototypeModelChoice,
  };
};

export default prototyperSlice.reducer;
